import { Fragment, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { useChildProfile } from '@/services/children';
import { GlobalHeader } from '@/components/GlobalHeader';
import { SchoolCalendarSheet } from '@/components/SchoolCalendarSheet';

type CommitmentType = 'sport' | 'music' | 'work' | 'other';

const COMMITMENT_COLORS: Record<CommitmentType, { bg: string; text: string }> = {
  sport: { bg: '#DCFCE7', text: '#15803D' },
  music: { bg: '#EDE9FE', text: '#6D28D9' },
  work:  { bg: '#DBEAFE', text: '#1D4ED8' },
  other: { bg: '#F3F4F6', text: '#4B5563' },
};

interface Commitment {
  commitment_id: string;
  label: string;
  type: CommitmentType;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

interface CommitmentsResponse {
  commitments: Commitment[];
}

interface FreeTimeSlot {
  day: string;
  start_time: string;
  end_time: string;
  label: string | null;
}

interface FreeTimeResponse {
  slots: FreeTimeSlot[];
}

// Display labels and their corresponding backend DayOfWeek values
const DAYS: { label: string; key: string }[] = [
  { label: 'Mon', key: 'monday'    },
  { label: 'Tue', key: 'tuesday'   },
  { label: 'Wed', key: 'wednesday' },
  { label: 'Thu', key: 'thursday'  },
  { label: 'Fri', key: 'friday'    },
];

const HOURS = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00',
];

// Actual backend TimetableEntry shape (no 'type' field; all entries are classes)
interface TimetableEntry {
  subject: string;
  day: string;       // lowercase full name: 'monday', 'tuesday', …
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

const timeLabelStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: '0.8rem',
  fontWeight: 600,
  textAlign: 'center',
  paddingTop: '6px',
};

const dayHeaderStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: '0.8rem',
  fontWeight: 600,
  textAlign: 'center',
};

export function TimetablePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useAuthStore();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const { data: profile } = useChildProfile(childId ?? '');
  const { data: timetableData, isLoading } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );
  const { data: commitmentsData } = useData<CommitmentsResponse>(
    childId ? `/children/${childId}/commitments` : null,
  );
  const { data: freeTimeData } = useData<FreeTimeResponse>(
    childId ? `/children/${childId}/free-time` : null,
  );

  const entries = timetableData?.entries ?? [];
  const commitments = commitmentsData?.commitments ?? [];
  const freeSlots = freeTimeData?.slots ?? [];

  // Match by hour component only — real timetables have non-round times like "10:07"
  const getEntry = (hour: string, dayKey: string): TimetableEntry | undefined =>
    entries.find((e) => e.day === dayKey && e.start_time.slice(0, 2) === hour.slice(0, 2));

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content">

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 20px' }}>
            {t('student_timetable.title')}
          </h1>

          {/* ── Action hub ── */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/student/timetable/commitments')}
              style={{ flex: 1, fontSize: '0.85rem', padding: '8px' }}
            >
              ⚽ {t('student_timetable.other')}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/student/timetable/free-time')}
              style={{ flex: 1, fontSize: '0.85rem', padding: '8px' }}
            >
              ⏳ {t('student_timetable.add_free')}
            </button>
          </div>

          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setCalendarOpen(true)}
            style={{ width: '100%', fontSize: '0.85rem', padding: '8px', marginBottom: '24px' }}
          >
            {t('school_calendar.view_button')}
          </button>

          {/* ── Loading ── */}
          {isLoading && (
            <div className="loading-skeleton" style={{ height: '260px', borderRadius: '12px' }} />
          )}

          {/* ── Empty state ── */}
          {!isLoading && entries.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '28px 20px' }}>
              <p style={{ fontSize: '2.5rem', margin: '0 0 12px' }}>📋</p>
              <p style={{ fontWeight: 700, color: 'var(--text-dark)', fontSize: '1rem', margin: '0 0 6px' }}>
                {t('student_timetable.empty_title')}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.5, margin: '0 0 24px' }}>
                {t('student_timetable.empty_desc')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate('/student/timetable/scan')}
                  style={{ width: '100%' }}
                >
                  {t('student_timetable.scan_btn')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/student/timetable/edit')}
                  style={{ width: '100%' }}
                >
                  {t('student_timetable.enter_manually')}
                </button>
              </div>
            </div>
          )}

          {/* ── Populated: timetable grid ── */}
          {!isLoading && entries.length > 0 && (
            <>
              <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '8px', flexShrink: 0 }}>
                <div className="timetable-grid">

                  {/* Day headers */}
                  <div />
                  {DAYS.map((d) => (
                    <div key={d.key} style={dayHeaderStyle}>{t(`student_timetable.days.${d.label}`)}</div>
                  ))}

                  {/* Hour rows */}
                  {HOURS.map((hour) => (
                    <Fragment key={hour}>
                      <div style={timeLabelStyle}>{hour}</div>
                      {DAYS.map((d) => {
                        const entry = getEntry(hour, d.key);
                        if (!entry) return <div key={d.key} />;
                        return (
                          <div
                            key={d.key}
                            className="subject-card"
                            title={entry.room ? `${entry.subject} · ${entry.room}` : entry.subject}
                          >
                            {entry.subject}
                            {entry.room && (
                              <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.7, marginTop: '2px' }}>
                                {entry.room}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </Fragment>
                  ))}

                </div>
              </div>

              {/* ── Activities ── */}
              {commitments.length > 0 && (
                <>
                  <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '24px 0 10px' }}>
                    {t('student_timetable.activities_heading')}
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {commitments.map((c) => {
                      const colors = COMMITMENT_COLORS[c.type] ?? COMMITMENT_COLORS.other;
                      return (
                        <div
                          key={c.commitment_id}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: 'white', borderRadius: '10px', padding: '12px 14px',
                            border: '1px solid var(--border)',
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-dark)' }}>{c.label}</span>
                              <span style={{
                                background: colors.bg, color: colors.text,
                                fontSize: '0.7rem', fontWeight: 600,
                                padding: '2px 7px', borderRadius: '999px',
                              }}>
                                {t(`student_timetable.commitment_types.${c.type}`)}
                              </span>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              {t(`student_timetable.days_full.${c.day_of_week}`, { defaultValue: c.day_of_week })}
                              {' · '}
                              {c.start_time}–{c.end_time}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/student/timetable/commitments')}
                    style={{ width: '100%', marginTop: '10px', fontSize: '0.85rem', padding: '8px' }}
                  >
                    {t('student_timetable.edit_activities')}
                  </button>
                </>
              )}

              {commitments.length === 0 && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/student/timetable/commitments')}
                  style={{ width: '100%', marginTop: '16px', fontSize: '0.85rem', padding: '8px' }}
                >
                  {t('student_timetable.add_activities')}
                </button>
              )}

              {/* ── Free time ── */}
              {freeSlots.length > 0 && (
                <>
                  <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '24px 0 10px' }}>
                    {t('student_timetable.free_time_heading')}
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {freeSlots.map((slot, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          background: 'white', borderRadius: '10px', padding: '12px 14px',
                          border: '1px dashed var(--success)',
                        }}
                      >
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-dark)' }}>
                            {slot.label ?? t('student_timetable.free_block')}
                          </p>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {t(`student_timetable.days_full.${slot.day}`, { defaultValue: slot.day })}
                            {' · '}
                            {slot.start_time}–{slot.end_time}
                          </p>
                        </div>
                        <span style={{ fontSize: '1.2rem' }}>✅</span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/student/timetable/free-time')}
                    style={{ width: '100%', marginTop: '10px', fontSize: '0.85rem', padding: '8px' }}
                  >
                    {t('student_timetable.edit_free_time')}
                  </button>
                </>
              )}

              {freeSlots.length === 0 && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/student/timetable/free-time')}
                  style={{ width: '100%', marginTop: '8px', fontSize: '0.85rem', padding: '8px' }}
                >
                  ⏳ {t('student_timetable.add_free')}
                </button>
              )}

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/student/timetable/edit')}
                style={{ width: '100%', marginTop: '16px' }}
              >
                ✏️ {t('student_timetable.edit')}
              </button>
            </>
          )}

        </div>

        <BottomTabBar activeTab="Timetable" />
      </div>

      <SchoolCalendarSheet
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        country={profile?.country}
      />
    </div>
  );
}
